import React, { useState, useEffect, useCallback } from 'react';
import WidgetWrapper from './WidgetWrapper';

// ─── Theme ─────────────────────────────────────────────────────────────────────
const C = {
  bg:       '#0d1117',
  card:     '#161b22',
  border:   '#30363d',
  cyan:     '#00e5ff',
  green:    '#22c55e',
  red:      '#ef4444',
  amber:    '#f59e0b',
  purple:   '#a855f7',
  text:     '#e2e8f0',
  sub:      '#8b949e',
};

// ─── Helpers ────────────────────────────────────────────────────────────────────
const fmtUptime = (s) => {
  if (!s) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const pct = (v, total) => total > 0 ? Math.min(100, Math.round((v / total) * 100)) : 0;

const barColor = (pct) => {
  if (pct >= 90) return C.red;
  if (pct >= 75) return C.amber;
  return C.green;
};

// ─── Main Widget ───────────────────────────────────────────────────────────────
const UniFiMonitorWidget = ({ darkMode = false, onError }) => {
  const [health,  setHealth]  = useState(null);
  const [devices, setDevices] = useState(null);
  const [clients, setClients] = useState(null);
  const [netInfo, setNetInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [tab,     setTab]     = useState('pool');
  const [lastUp,  setLastUp]  = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const opts = { headers: { 'Content-Type': 'application/json' } };
      const [h, d, c, n] = await Promise.all([
        fetch('/api/unifi/health', opts).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))).catch(() => null),
        fetch('/api/unifi/devices', opts).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))).catch(() => null),
        fetch('/api/unifi/clients', opts).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))).catch(() => null),
        fetch('/api/unifi/network', opts).then(r => r.ok ? r.json() : Promise.reject(() => null)).catch(() => null),
      ]);
      setHealth(h); setDevices(d); setClients(c); setNetInfo(n);
      setLastUp(new Date());
      if (!h && !d && !c) setError('UniFi unreachable');
      else setError(null);
    } catch (e) {
      setError(e.message);
      onError?.('UniFi', e.message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { const id = setInterval(fetchAll, 30000); return () => clearInterval(id); }, [fetchAll]);

  // ── Normalise data ───────────────────────────────────────────────────────
  const devs = devices?.data || [];
  const cliList = clients?.clients || [];

  // NGX VLAN 10 clients
  const ngxClients = cliList.filter(c =>
    (c.gw_vlan === 10 || c.vlan === 10 || c.network_name === 'NGX') ||
    (c.ip && (c.ip.startsWith('192.168.10.') || c.ip.startsWith('192.168.11.')))
  );

  // Network info
  const subnet = netInfo?.subnet || '192.168.10.1/23';
  const vlan = netInfo?.vlan || 10;
  const totalIPs = netInfo?.totalIPs || 512;
  const dhcpTotal = netInfo?.dhcpPool || 244;
  const dhcpUsed = ngxClients.length;
  const gatewayIPs = netInfo?.gatewayIPs || 10;

  // Device counts
  const onlineDevs = (health?.devices?.online) || devs.filter(d => d.state === 1).length;
  const totalDevs = (health?.devices?.total) || devs.length;
  const offlineDevs = (health?.devices?.offline) || devs.filter(d => d.state !== 1).length;

  // Find gateway
  const gateway = devs.find(d => d.type === 'ugw' || d.model?.includes('UDM')) || {};
  const gwUptime = gateway.uptime;

  return (
    <WidgetWrapper
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <span style={{ fontSize: 14 }}>📶</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.cyan }}>NGX Network</span>
          <span style={{ fontSize: 10, color: C.sub }}>VLAN {vlan}</span>
          {lastUp && <span style={{ fontSize: 9, color: C.sub }}>· {lastUp.toLocaleTimeString()}</span>}
          <button onClick={fetchAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.sub, marginLeft: 6 }}>⟳</button>
        </div>
      }
      loading={loading}
      error={error}
      darkMode
    >
      {/* ── Tab bar ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
        {[
          { key: 'pool', label: `📊 IP Pool (${dhcpUsed}/${dhcpTotal})` },
          { key: 'clients', label: `👤 Clients (${ngxClients.length})` },
          { key: 'devices', label: `🖥️ UniFi Devices (${onlineDevs}/${totalDevs})` },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '6px 12px', fontSize: 10, fontWeight: 600,
              color: tab === t.key ? C.cyan : C.sub,
              borderBottom: `2px solid ${tab === t.key ? C.cyan : 'transparent'}`,
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════ POOL TAB ══════════ */}
      {tab === 'pool' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* DHCP Pool Usage */}
          <div style={{ background: 'rgba(0,229,255,0.05)', border: `1px solid ${C.cyan}44`, borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.08em' }}>VLAN {vlan}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 2 }}>{subnet}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: barColor(pct(ngxClients.length, totalIPs)) }}>{ngxClients.length}</div>
                <div style={{ fontSize: 10, color: C.sub }}>of {totalIPs} IPs</div>
              </div>
            </div>
            {/* Progress bar */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.sub, marginBottom: 4 }}>
                <span>Network Usage</span>
                <span style={{ color: barColor(pct(ngxClients.length, totalIPs)) }}>{pct(ngxClients.length, totalIPs)}%</span>
              </div>
              <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct(ngxClients.length, totalIPs)}%`, background: barColor(pct(ngxClients.length, totalIPs)), borderRadius: 3 }} />
              </div>
            </div>
            {/* Stats */}
            <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11 }}>
              <div><span style={{ color: C.sub }}>In Use:</span> <span style={{ color: C.text, fontWeight: 600 }}>{ngxClients.length}</span></div>
              <div><span style={{ color: C.sub }}>DHCP Pool:</span> <span style={{ color: C.text, fontWeight: 600 }}>{dhcpUsed}/{dhcpTotal}</span></div>
              <div><span style={{ color: C.sub }}>Free:</span> <span style={{ color: C.green, fontWeight: 600 }}>{totalIPs - ngxClients.length}</span></div>
            </div>
          </div>

          {/* WAN/ISP Info */}
          {health?.gateway?.wan && (
            <div style={{ background: 'rgba(168,85,247,0.05)', border: `1px solid ${C.purple}44`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>🌐</span>
                  <div>
                    <div style={{ fontSize: 10, color: C.sub, textTransform: 'uppercase' }}>WAN / ISP</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginTop: 2 }}>
                      {health.gateway.wan.ip || 'Connected'}
                    </div>
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: health.gateway.wan.up ? C.green : C.red }}>
                  {health.gateway.wan.up ? '● ONLINE' : '○ OFFLINE'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {health.gateway.wan.speed && (
                  <div style={{ fontSize: 11 }}>
                    <span style={{ color: C.sub }}>Speed:</span>{' '}
                    <span style={{ color: C.text, fontWeight: 600 }}>{health.gateway.wan.speed} Mbps</span>
                  </div>
                )}
                {health.gateway.wan.latency != null && (
                  <div style={{ fontSize: 11 }}>
                    <span style={{ color: C.sub }}>Latency:</span>{' '}
                    <span style={{ color: health.gateway.wan.latency < 30 ? C.green : health.gateway.wan.latency < 60 ? C.amber : C.red, fontWeight: 600 }}>
                      {health.gateway.wan.latency}ms
                    </span>
                  </div>
                )}
                {health.gateway.wan.availability != null && (
                  <div style={{ fontSize: 11 }}>
                    <span style={{ color: C.sub }}>Avail:</span>{' '}
                    <span style={{ color: health.gateway.wan.availability === 100 ? C.green : C.amber, fontWeight: 600 }}>
                      {health.gateway.wan.availability}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Device Summary */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, color: C.sub, textTransform: 'uppercase', marginBottom: 6 }}>Devices</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: onlineDevs === totalDevs ? C.green : C.amber }}>{onlineDevs}</span>
                <span style={{ fontSize: 14, color: C.sub }}>/ {totalDevs}</span>
              </div>
              {offlineDevs > 0 && <div style={{ fontSize: 10, color: C.red, marginTop: 2 }}>{offlineDevs} offline</div>}
            </div>
            <div style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, color: C.sub, textTransform: 'uppercase', marginBottom: 6 }}>Clients</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: C.cyan }}>{health?.clients ?? ngxClients.length}</div>
              <div style={{ fontSize: 10, color: C.sub, marginTop: 2 }}>on NGX VLAN</div>
            </div>
            {health?.gateway?.uptime && (
              <div style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 10, color: C.sub, textTransform: 'uppercase', marginBottom: 6 }}>Gateway</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.amber }}>{fmtUptime(health.gateway.uptime)}</div>
                <div style={{ fontSize: 10, color: C.sub, marginTop: 2 }}>UDM Pro Max</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════ CLIENTS TAB ══════════ */}
      {tab === 'clients' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.cyan }}>{ngxClients.length}</div>
              <div style={{ fontSize: 9, color: C.sub }}>Total</div>
            </div>
            <div style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#00e5ff' }}>{ngxClients.filter(c => c.is_wired).length}</div>
              <div style={{ fontSize: 9, color: C.sub }}>Wired</div>
            </div>
            <div style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.purple }}>{ngxClients.filter(c => !c.is_wired).length}</div>
              <div style={{ fontSize: 9, color: C.sub }}>WiFi</div>
            </div>
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {ngxClients.length === 0 && <div style={{ textAlign: 'center', padding: 20, color: C.sub }}>No clients found</div>}
            {ngxClients.sort((a, b) => (b.uptime || 0) - (a.uptime || 0)).slice(0, 20).map((c, i) => (
              <div key={c.mac || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 12 }}>{c.is_wired ? '🔌' : '📶'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.hostname || c.name || c.ip || 'Unknown'}
                  </div>
                  <div style={{ fontSize: 9, color: C.sub }}>{c.ip} · {fmtUptime(c.uptime)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════ DEVICES TAB ══════════ */}
      {tab === 'devices' && (
        <div style={{ maxHeight: 250, overflowY: 'auto' }}>
          {devs.map(d => {
            const online = d.state === 1;
            const typeIcon = d.type === 'uap' ? '📡' : d.type === 'usw' ? '🔌' : d.type === 'ugw' ? '🖥️' : '❓';
            const typeName = d.type === 'uap' ? 'AP' : d.type === 'usw' ? 'Switch' : d.type === 'ugw' ? 'Gateway' : d.type;
            const sys = d.system_stats || {};
            
            return (
              <div key={d.mac} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>{online ? '🟢' : '🔴'}</span>
                <span style={{ fontSize: 16 }}>{typeIcon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.name || d.hostname || d.model}
                  </div>
                  <div style={{ fontSize: 9, color: C.sub }}>{typeName} · {fmtUptime(d.uptime)}</div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 10, textAlign: 'center' }}>
                  {sys.cpu && <div><span style={{ color: C.sub }}>CPU</span><br/><span style={{ color: C.text, fontWeight: 600 }}>{sys.cpu}%</span></div>}
                  {sys.mem && <div><span style={{ color: C.sub }}>MEM</span><br/><span style={{ color: C.text, fontWeight: 600 }}>{sys.mem}%</span></div>}
                  {d.num_sta != null && <div><span style={{ color: C.sub }}>Clients</span><br/><span style={{ color: C.cyan, fontWeight: 600 }}>{d.num_sta}</span></div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WidgetWrapper>
  );
};

UniFiMonitorWidget.widgetId = 'unifi';
UniFiMonitorWidget.title = '📶 UniFi Network';
UniFiMonitorWidget.defaultSize = { cols: 6 };
UniFiMonitorWidget.category = 'infrastructure';

export default UniFiMonitorWidget;